/**
 * The studio's library, in PCake.
 *
 * CreativeStudio's editor talks to its library through this module's function names (it picks
 * between a device store and a Supabase bucket). In PCake there is one library: the app's own
 * storage bucket, reached through utils/storageUtils exactly as the old studio did, under
 * istudio/<uid>/ (and istudio/ itself for files Admin/Super saved before this version).
 *
 * Every function keeps the signature the editor calls; the differences from storageUtils are
 * handled here so the editor file stays as close to CreativeStudio's as possible:
 *   • listings go one folder deep, so an Admin/Super library at istudio/ also shows what is in
 *     each person's istudio/<uid>/ folder (the old studio only saw the top level);
 *   • readObjectContent returns { downloadUrl, blob? } for pictures and { blob } for the
 *     .cstudio.json sheet files, which storageUtils returns as text;
 *   • a browser that blocks firebasestorage.googleapis.com gets the bytes through the
 *     storageImage Cloud Function instead (see services/storageFallback).
 */
import * as store from '../../utils/storageUtils';
import { proxiedImageUrl, isFirebaseStorageUrl, isStorageBlocked } from '../../services/storageFallback';
import { getFirebase } from '../../services/firebase';
import { FN_BASE } from '../../services/fnBase';

export const LOCAL_BUCKET = 'pcake';

/** Always the app's bucket — there is no device-only library in PCake. */
export const usingCloud = () => true;
export const cloudOffline = () => false;
export const cloudSignedIn = () => true;

export const getEnvStorageBucket = () => store.getEnvStorageBucket();

// Read links handed out with the last listing, by path — so opening a picture from the library
// asks nothing more of the server.
const urls = new Map();

/**
 * The library listing, from the listStudioLibrary Cloud Function: one call, every file under the
 * folder (Admin/Super: istudio/ and everyone's folder under it), each with its read link. No
 * Firebase Storage request is made from the browser, which some desktop browsers block outright.
 */
const listLibrary = async (prefix, bucket) => {
  const user = getFirebase().auth?.currentUser;
  if (!user) throw new Error('Sign in to PCake to open the library.');
  const res = await fetch(`${FN_BASE}/listStudioLibrary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
    body: JSON.stringify({ prefix, bucket: bucket || store.getEnvStorageBucket() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error?.message || `Library could not be read (${res.status})`);
  for (const it of data.items) if (it.url) urls.set(it.name, it.url);
  return { items: data.items.map((it) => ({ ...it, id: it.name })), prefixes: [], bucket };
};

// Test-env users have no Cloud Functions behind them (storageUtils uses the SDK there).
const viaFunction = () => {
  try { return store.getEnvStorageBucket() === 'pcakeds_csv_backup1'; } catch { return true; }
};
const listDeep = async (lister, prefix, bucket) => {
  const top = await lister(prefix, bucket);
  const items = [...(top.items || [])];
  for (const p of top.prefixes || []) {
    try { items.push(...((await lister(p.name, bucket)).items || [])); } catch { /* skip */ }
  }
  return { items, prefixes: top.prefixes || [], bucket };
};

export const listObjectsInFolder = (prefix = '', bucket) =>
  (viaFunction() ? listLibrary(prefix, bucket) : listDeep(store.listObjectsInFolder, prefix, bucket));
export const listObjectsWithMetadata = (prefix = '', bucket) =>
  (viaFunction() ? listLibrary(prefix, bucket) : listDeep(store.listObjectsWithMetadata, prefix, bucket));

/** Fetch bytes, falling back to the Cloud Functions proxy when Firebase Storage is blocked. */
const fetchBlob = async (url) => {
  try {
    const res = await fetch(url);
    if (res.ok) return await res.blob();
  } catch { /* blocked or offline — try the proxy */ }
  if (isFirebaseStorageUrl(url)) {
    try {
      const res = await fetch(proxiedImageUrl(url));
      if (res.ok) return await res.blob();
    } catch { /* give up quietly — the URL alone still places the picture */ }
  }
  return null;
};

// A browser that blocks Firebase Storage gets the picture through the storageImage function, so
// library previews and pictures placed on the sheet show there too.
export const getDownloadUrl = async (objectName, bucket) => {
  // Permanent (token) links: the studio stores them — in chat messages and saved sheets.
  const url = urls.get(String(objectName))
    || await store.getPermanentUrl(objectName, bucket).catch(() => store.getDownloadUrl(objectName, bucket));
  if (isFirebaseStorageUrl(url) && /\.(jpe?g|png|gif|webp|bmp)$/i.test(String(objectName)) && await isStorageBlocked()) {
    return proxiedImageUrl(url);
  }
  return url;
};
export const getSignedUrl = getDownloadUrl;

export const readObjectContent = async (objectName, bucket) => {
  const path = String(objectName || '');
  // The sheet's .cstudio.json comes back from storageUtils (its Cloud Function) as text.
  if (/\.json$/i.test(path)) {
    const res = await store.readObjectContent(objectName, bucket);
    const text = typeof res === 'string' ? res : JSON.stringify(res ?? null);
    const blob = new Blob([text], { type: 'application/json' });
    return { path, name: path, blob, size: blob.size, contentType: blob.type };
  }
  const downloadUrl = await getDownloadUrl(objectName, bucket);
  const base = { downloadUrl, path, name: path, isMedia: true };
  const blob = downloadUrl ? await fetchBlob(downloadUrl) : null;
  return blob ? { ...base, blob, size: blob.size, contentType: blob.type } : base;
};

export const uploadToStorage = (fileName, file, contentType, bucket) =>
  store.uploadToStorage(fileName, file, contentType, bucket);

export const renameInStorage = async (oldPath, newPath, bucket) => {
  const out = await store.renameInStorage(oldPath, newPath, bucket);
  urls.delete(String(oldPath));
  return out;
};
export const deleteFromStorage = async (fileName, bucket) => {
  const out = await store.deleteFromStorage(fileName, bucket);
  urls.delete(String(fileName));
  return out;
};

/** The editor sometimes holds a download URL where a path is expected. */
export const resolvePath = (pathOrUrl) => {
  const s = String(pathOrUrl || '');
  const m = s.match(/\/o\/([^?]+)/);
  if (m && isFirebaseStorageUrl(s)) { try { return decodeURIComponent(m[1]); } catch { return m[1]; } }
  return s.replace(/^\/+/, '');
};
